import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { HandlingPartySelect } from '@client/components/_shared/selectors/common/HandlingPartySelect';
import { UserSelector } from '@client/components/_shared/selectors/users/UserSelector';
import { ArrayInput } from '@client/components/_ui/data-entry/ArrayInput';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { TipTapInput } from '@client/components/_ui/data-entry/TipTap';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ControlsTableDrawer } from '@client/components/controls/ControlsTableDrawer';
import { BreachExamples } from '@client/components/obligations/breach-examples';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useSafePath } from '@client/hooks/utils/useSafePath';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { requiredFieldRule } from '@client/utils';
import { OBLIGATION_STATUSES, USER_SETTABLE_OBLIGATION_STATUS_KEYS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Form, Input, InputNumber, Row, Select, Table } from 'antd';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/compliance/obligations/add')({
    staticData: {
        breadcrumb: {
            title: 'New obligation',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ObligationAdd />
        </PageController>
    );
}

type ObligationInput = RouterInputs['obligation']['createObligation'];

function ObligationAdd() {
    const orgId = useOrgId();
    const { data: countries, isLoading: countriesLoading } = useConfigServiceLoader((service) =>
        service.fetchCountryList(),
    );
    const { data: legislationItems, isPending: legislationLoading } = trpc.legislation.listLegislationItems.useQuery({
        orderBy: { name: 'asc' },
        limit: 1000,
    });
    const { data: lastObligationData, isLoading: loading } = trpc.obligation.getLastObligationOrder.useQuery();
    const { mutate, isPending } = trpc.obligation.createObligation.useMutation();
    const navigate = useNavigate();

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedControlIds, setSelectedControlIds] = useState<bigint[]>([]);
    const [breachExamples, setBreachExamples] = useState<string[]>([]);

    // Query selected controls for display
    const { data: selectedControlsData } = trpc.control.listControls.useQuery(
        {
            where: {
                id: {
                    in: selectedControlIds.length > 0 ? selectedControlIds : [-1n], // -1n to avoid empty query
                },
            },
            limit: 1000,
        },
        {
            enabled: selectedControlIds.length > 0,
        },
    );

    const [form] = Form.useForm<ObligationInput>();
    const safePath = useSafePath<ObligationInput>();
    const legislationId = Form.useWatch('legislationId', form);
    const handlingPartyIds = Form.useWatch('handlingPartyIds', form);
    const effectiveDateFrom = Form.useWatch('effectiveDateFrom', form);
    const effectiveDateTo = Form.useWatch('effectiveDateTo', form);

    useEffect(() => {
        if (lastObligationData) {
            form.setFieldsValue({
                order: (lastObligationData.order || 0) + 1,
            });
        }
    }, [lastObligationData, form]);

    function onFinish(values: ObligationInput) {
        message.loading('Saving...');

        mutate(
            {
                ...values,
                breachExamples,
                controlIds: selectedControlIds,
            },
            {
                onSuccess({ id }) {
                    message.success('Saved.');

                    navigate({
                        to: '/console/$orgId/compliance/obligations/details/$id/manage',
                        params: {
                            id,
                            orgId,
                        },
                    });
                },
                onError(error) {
                    errorMessage.show(error);
                },
            },
        );
    }

    const handleSelectControls = async (selectedKeys: (string | number | bigint)[]) => {
        setSelectedControlIds(selectedKeys.map(BigInt));
        setDrawerVisible(false);
    };

    return (
        <>
            <ControlsTableDrawer
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                selectedControls={selectedControlIds.map((id) => ({ id }))}
                onSelectionSave={handleSelectControls}
                showAddControl
            />

            <Form form={form} id="obligationEdit" layout="vertical" onFinish={onFinish}>
                <Row gutter={[16, 16]}>
                    <Col span={16}>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card title="New Obligation" loading={loading}>
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} sm={24} md={6}>
                                            <Form.Item
                                                label="ID"
                                                name={safePath('order')}
                                                required
                                                rules={requiredFieldRule}
                                            >
                                                <InputNumber className="w-full" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={6}>
                                            <Form.Item
                                                label="Identification code"
                                                name={safePath('identificationCode')}
                                                rules={requiredFieldRule}
                                                required
                                            >
                                                <Input
                                                    prefix={
                                                        legislationItems?.rows?.find(
                                                            (item) => item.id === legislationId,
                                                        )?.idPrefix || ''
                                                    }
                                                    className="w-full"
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} sm={24} md={12}>
                                            <Form.Item
                                                label="Status"
                                                name={safePath('status')}
                                                rules={requiredFieldRule}
                                            >
                                                <Select
                                                    options={USER_SETTABLE_OBLIGATION_STATUS_KEYS.map((key) => ({
                                                        label: OBLIGATION_STATUSES[key],
                                                        value: key,
                                                    }))}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12}>
                                            <Form.Item label="Handling parties" name={safePath('handlingPartyIds')}>
                                                <HandlingPartySelect
                                                    forOrgAdminShowAll
                                                    onChange={() => form.resetFields(['ownerId'])}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12}>
                                            <Form.Item label="Obligation owner" name={safePath('ownerId')}>
                                                <UserSelector handlingPartyIds={handlingPartyIds} placeholder="" />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} sm={24} md={12}>
                                            <Form.Item
                                                label="Country"
                                                name={safePath('country')}
                                                rules={requiredFieldRule}
                                            >
                                                <Select
                                                    loading={countriesLoading}
                                                    showSearch
                                                    options={countries?.map((item) => ({
                                                        label: item,
                                                        value: item,
                                                    }))}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={24} md={12}>
                                            <Form.Item
                                                label="Legislation"
                                                name={safePath('legislationId')}
                                                rules={requiredFieldRule}
                                            >
                                                <Select
                                                    loading={legislationLoading}
                                                    showSearch
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '')
                                                            .toString()
                                                            .toLowerCase()
                                                            .includes(input.toLowerCase())
                                                    }
                                                    options={legislationItems?.rows?.map(({ name, id }) => ({
                                                        label: name,
                                                        value: id,
                                                    }))}
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} sm={24} md={12}>
                                            <Form.Item
                                                label="Effective From"
                                                name="effectiveDateFrom"
                                                dependencies={['effectiveDateTo']}
                                                rules={[
                                                    {
                                                        validator(_, value) {
                                                            if (!value || !effectiveDateTo) {
                                                                return Promise.resolve();
                                                            }
                                                            if (value <= effectiveDateTo) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(
                                                                new Error(
                                                                    'Effective From date cannot be later than Effective To date',
                                                                ),
                                                            );
                                                        },
                                                    },
                                                ]}
                                            >
                                                <DatePicker
                                                    className="w-full"
                                                    maxDate={effectiveDateTo ? effectiveDateTo : undefined}
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} sm={24} md={12}>
                                            <Form.Item
                                                label="Effective To"
                                                name="effectiveDateTo"
                                                dependencies={['effectiveDateFrom']}
                                                rules={[
                                                    {
                                                        validator(_, value) {
                                                            if (!value || !effectiveDateFrom) {
                                                                return Promise.resolve();
                                                            }
                                                            if (value >= effectiveDateFrom) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(
                                                                new Error(
                                                                    'Effective To date cannot be earlier than Effective From date',
                                                                ),
                                                            );
                                                        },
                                                    },
                                                ]}
                                            >
                                                <DatePicker
                                                    className="w-full"
                                                    minDate={effectiveDateFrom ? effectiveDateFrom : undefined}
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col span={24}>
                                            <Form.Item
                                                label="Core obligation"
                                                name={safePath('coreObligation')}
                                                rules={requiredFieldRule}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item label="What it means" name={safePath('whatItMeans')}>
                                                <TipTapInput
                                                    aiAssistant={{
                                                        relatedFieldLabel: 'Obligation - What it means',
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                label="How we are complying"
                                                name={safePath('howWeAreComplying')}
                                            >
                                                <TipTapInput
                                                    aiAssistant={{
                                                        relatedFieldLabel: 'Obligation - How we are complying',
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col span={24}>
                                <Card title="Source of obligation">
                                    <Row gutter={[16, 16]}>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Sub obligation / section / paragraph"
                                                name={safePath('section')}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item label="Source" name={safePath('source')}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                label="Exact wording from the legislation"
                                                name={safePath('legislationDescription')}
                                            >
                                                <TipTapInput
                                                    aiAssistant={{
                                                        relatedFieldLabel:
                                                            'Obligation - Exact wording from the legislation',
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item label="Austlii link">
                                                <ArrayInput
                                                    name={safePath('austLIIURLs')}
                                                    fields={[
                                                        {
                                                            label: 'Name',
                                                            name: 'name',
                                                            children: <Input placeholder="Name" />,
                                                        },
                                                        {
                                                            label: 'URL',
                                                            name: 'url',
                                                            rules: [
                                                                ...requiredFieldRule,
                                                                {
                                                                    type: 'url',
                                                                },
                                                            ],
                                                            children: <Input placeholder="URL" />,
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item label="Federal Register of Legislation link">
                                                <ArrayInput
                                                    name={safePath('federalRegisterOfLegislationURLs')}
                                                    fields={[
                                                        {
                                                            label: 'Name',
                                                            name: 'name',
                                                            children: <Input placeholder="Name" />,
                                                        },
                                                        {
                                                            label: 'URL',
                                                            name: 'url',
                                                            rules: [
                                                                ...requiredFieldRule,
                                                                {
                                                                    type: 'url',
                                                                },
                                                            ],
                                                            children: <Input placeholder="URL" />,
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col span={24}>
                                <Card title="Regulatory guidance">
                                    <Row gutter={[16, 16]}>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Regulatory guidance"
                                                name={safePath('regulatoryGuidance')}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item label="Regulator" name={safePath('regulator')}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                label="Exact wording from the Regulatory guide"
                                                name={safePath('regulatoryGuidanceDescription')}
                                            >
                                                <TipTapInput
                                                    aiAssistant={{
                                                        relatedFieldLabel:
                                                            'Obligation - Exact wording from the Regulatory guide',
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item label="Links">
                                                <ArrayInput
                                                    name={safePath('regulatoryGuidanceURLs')}
                                                    fields={[
                                                        {
                                                            label: 'Name',
                                                            name: 'name',
                                                            children: <Input placeholder="Name" />,
                                                        },
                                                        {
                                                            label: 'URL',
                                                            name: 'url',
                                                            rules: [
                                                                ...requiredFieldRule,
                                                                {
                                                                    type: 'url',
                                                                },
                                                            ],
                                                            children: <Input placeholder="URL" />,
                                                        },
                                                    ]}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col span={24}>
                                <BreachExamples breachExamples={breachExamples || []} onSave={setBreachExamples} />
                            </Col>
                            <Col span={24} className="flex justify-between">
                                <Button.Link
                                    to="/console/$orgId/compliance/obligations"
                                    params={{ orgId }}
                                    icon={<ArrowLeftOutlined />}
                                    variant="secondary"
                                >
                                    Back
                                </Button.Link>
                                <Button type="primary" htmlType="submit" form="obligationEdit" loading={isPending}>
                                    Save
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={8}>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card
                                    title="Key controls"
                                    styles={{ body: { padding: 10 } }}
                                    extra={
                                        <Button
                                            type="secondary"
                                            icon={<PlusOutlined />}
                                            onClick={() => {
                                                setDrawerVisible(true);
                                            }}
                                        >
                                            Add control
                                        </Button>
                                    }
                                >
                                    <Table
                                        size="small"
                                        dataSource={selectedControlsData?.rows || []}
                                        scroll={{ x: 'max-content' }}
                                        columns={[
                                            {
                                                title: 'No.',
                                                dataIndex: 'id',
                                                sorter: true,
                                                width: 40,
                                                render: String,
                                            },
                                            {
                                                title: 'Control Name',
                                                dataIndex: 'name',
                                                render: (value) => <ComponentPreview>{value}</ComponentPreview>,
                                            },
                                            {
                                                title: 'Owner',
                                                dataIndex: 'ownerId',
                                                render: (value) => <User userId={value} />,
                                            },
                                            {
                                                render: (_, record) => (
                                                    <Button.Delete
                                                        onConfirm={(onSuccess) => {
                                                            setSelectedControlIds(
                                                                selectedControlIds.filter((id) => id !== record.id),
                                                            );
                                                            onSuccess();
                                                        }}
                                                    />
                                                ),
                                            },
                                        ]}
                                        pagination={false}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Form>
        </>
    );
}
